import React, { useState } from "react";
import { useTransferSharesMutation } from "../api";
import { UserAutocomplete } from "./UserAutocomplete";
import { useNotification } from "../hooks/useNotification";

interface TransferSharesFormProps {
    sharesType: 0 | 1; // 0 - подарочные, 1 - купленные
    onSuccess?: () => void;
}

export const TransferSharesForm: React.FC<TransferSharesFormProps> = ({
    sharesType,
    onSuccess,
}) => {
    const { showError, showSuccess } = useNotification();
    const [transferFromUserId, setTransferFromUserId] = useState<string>("");
    const [transferToUserId, setTransferToUserId] = useState<string>("");
    const [transferQuantity, setTransferQuantity] = useState<string>("");
    const [isTransferring, setIsTransferring] = useState(false);

    const [transferShares, { isLoading: isTransferringShares }] =
        useTransferSharesMutation();

    const handleTransferShares = async () => {
        if (!transferFromUserId || !transferToUserId || !transferQuantity) {
            showError("Заполните все поля");
            return;
        }

        const quantity = parseInt(transferQuantity, 10);
        if (isNaN(quantity) || quantity <= 0) {
            showError("Введите корректное количество акций");
            return;
        }

        const fromUserId = parseInt(transferFromUserId, 10);
        const toUserId = parseInt(transferToUserId, 10);

        if (isNaN(fromUserId) || isNaN(toUserId)) {
            showError("Выберите пользователей");
            return;
        }

        if (fromUserId === toUserId) {
            showError("Пользователи должны быть разными");
            return;
        }

        setIsTransferring(true);
        try {
            const result = await transferShares({
                from_user_id: fromUserId,
                quantity: quantity,
                to_user_id: toUserId,
                shares_type: sharesType,
            }).unwrap();

            if (result.success) {
                showSuccess(
                    `Акции успешно перенесены: ${result.data.transferred} шт.`
                );
                // Очищаем форму
                setTransferFromUserId("");
                setTransferToUserId("");
                setTransferQuantity("");
                // Вызываем callback для обновления списка
                onSuccess?.();
            }
        } catch (error: any) {
            showError(
                error?.data?.message ||
                    error?.message ||
                    "Ошибка при переносе акций"
            );
        } finally {
            setIsTransferring(false);
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Перенос акций
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        От пользователя
                    </label>
                    <UserAutocomplete
                        value={transferFromUserId}
                        onChange={setTransferFromUserId}
                        onUserSelect={(user) => {
                            setTransferFromUserId(user.id.toString());
                        }}
                        placeholder="ID, email, имя..."
                        disabled={isTransferring || isTransferringShares}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Количество акций
                    </label>
                    <input
                        type="number"
                        min="1"
                        value={transferQuantity}
                        onChange={(e) => setTransferQuantity(e.target.value)}
                        placeholder="Введите количество"
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        disabled={isTransferring || isTransferringShares}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        К пользователю
                    </label>
                    <UserAutocomplete
                        value={transferToUserId}
                        onChange={setTransferToUserId}
                        onUserSelect={(user) => {
                            setTransferToUserId(user.id.toString());
                        }}
                        placeholder="ID, email, имя..."
                        disabled={isTransferring || isTransferringShares}
                    />
                </div>
                <div className="flex items-end">
                    <button
                        onClick={handleTransferShares}
                        disabled={
                            isTransferring ||
                            isTransferringShares ||
                            !transferFromUserId ||
                            !transferToUserId ||
                            !transferQuantity
                        }
                        className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isTransferring || isTransferringShares
                            ? "Перенос..."
                            : "Перенести"}
                    </button>
                </div>
            </div>
        </div>
    );
};

